import { SettingsWrapper } from "../../../Billing/partials/SettingsWrapper";
import { FormLabel, Password } from "../../../../components";
import { Button, FormControl, Stack } from "@mui/material";
import { PasswordRequirements } from "../../../SignUp/partials/PasswordRequirements";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  changePasswordValidationSchema,
  WRONG_OLD_PASSWORD_ERR,
} from "../../../../constants/accountSettings";
import { changePassword } from "../../../../services/apiV2";
import { axiosErrorMatches } from "../../../../helpers";
import { enqueueSnackbar } from "notistack";
import {
  PASSWORD_CHANGED_SUCCESSFULLY,
  UNABLE_TO_CHANGE_PASSWORD,
  WRONG_OLD_PASSWORD,
} from "../../../../constants";

type TFormFields = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export const ChangePassword = () => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<TFormFields>({
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    resolver: zodResolver(changePasswordValidationSchema),
    reValidateMode: "onSubmit",
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      await changePassword(data);
      enqueueSnackbar(PASSWORD_CHANGED_SUCCESSFULLY, { variant: "success" });
      reset();
    } catch (e) {
      if (axiosErrorMatches(e, WRONG_OLD_PASSWORD_ERR)) {
        enqueueSnackbar(WRONG_OLD_PASSWORD, { variant: "error" });
      } else {
        enqueueSnackbar(UNABLE_TO_CHANGE_PASSWORD, { variant: "error" });
      }
    }
  });

  return (
    <SettingsWrapper
      heading="Change Password"
      subHeading="Create a new password for your account"
    >
      <Stack component="form" gap={1.5} onSubmit={onSubmit} noValidate>
        <Controller
          control={control}
          name="oldPassword"
          render={({ field, fieldState }) => (
            <FormControl disabled={isSubmitting} error={fieldState.invalid}>
              <FormLabel htmlFor="oldPassword" sx={{ fontSize: 13 }}>
                Old Password
              </FormLabel>
              <Password
                {...field}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={isSubmitting}
                id="oldPassword"
                placeholder="Old password"
                size="small"
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="newPassword"
          render={({ field, fieldState }) => (
            <FormControl disabled={isSubmitting} error={fieldState.invalid}>
              <FormLabel
                htmlFor="newPassword"
                sx={{ fontSize: 13 }}
                renderTooltipContent={() => <PasswordRequirements />}
              >
                New Password
              </FormLabel>
              <Password
                {...field}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={isSubmitting}
                id="newPassword"
                placeholder="New password"
                size="small"
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="confirmPassword"
          render={({ field, fieldState }) => (
            <FormControl disabled={isSubmitting} error={fieldState.invalid}>
              <FormLabel htmlFor="confirmPassword" sx={{ fontSize: 13 }}>
                Confirm Password
              </FormLabel>
              <Password
                {...field}
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={isSubmitting}
                id="confirmPassword"
                placeholder="Confirm password"
                size="small"
              />
            </FormControl>
          )}
        />
        <Stack flexDirection="row" justifyContent="flex-end" gap={1}>
          <Button
            disabled={isSubmitting || !isDirty}
            type="reset"
            onClick={() => reset()}
            sx={{ px: 2, py: 2 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={!isDirty}
            loading={isSubmitting}
            sx={{ px: 2, py: 2 }}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </SettingsWrapper>
  );
};
